"use client"

import type React from "react"
import { useState, useEffect, useRef } from "react"
import { Card, CardBody, CardHeader, Button, Modal, ModalHeader, ModalBody, Badge, Spinner, Row, Col } from "reactstrap"
import {
  FaIdBadge,
  FaHistory,
  FaUser,
  FaBuilding,
  FaUsers,
  FaCertificate,
  FaCreditCard,
  FaArrowLeft,
  FaPrint,
  FaCheckCircle,
  FaLock,
} from "react-icons/fa"
import { toast } from "react-toastify"
import Swal from "sweetalert2"
import DataTable from "react-data-table-component"
import ProfessionalBadgeDesigner from "./professional-badge-designer"
import EnhancedBulkBadgePrinter from "./bulkprint"

import {
  printOrderService,
  type PrintOrder,
  type OrderStaff,
  type OrderStaffResponse,
  type CompletionStatus,
} from "../../../Service/print-order-service"
import { ExhibitorRelatedService, type BadgeHistoryItem } from "../../../Service/exhibitorsRelatedService"
import AuthService from "../../../Service/AuthService"

interface Props {
  order: PrintOrder
  onBackToSelection: () => void
  onPrintingComplete: () => void
}

const PrintingStaffListUpdated: React.FC<Props> = ({ order, onBackToSelection, onPrintingComplete }) => {
  // Data state
  const [staff, setStaff] = useState<OrderStaff[]>([])
  const [orderDetails, setOrderDetails] = useState<OrderStaffResponse["order"] | null>(null)
  const [summary, setSummary] = useState<OrderStaffResponse["summary"] | null>(null)
  const [completionStatus, setCompletionStatus] = useState<CompletionStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingCompletion, setLoadingCompletion] = useState(false)
  const [isPrivileged, setIsPrivileged] = useState(false)

  // Modal state
  const [historyModal, setHistoryModal] = useState(false)
  const [bulkPrintModal, setBulkPrintModal] = useState(false)
  // Badge designer state
  const [badgeDesignerModal, setBadgeDesignerModal] = useState(false)
  const [selectedStaff, setSelectedStaff] = useState<OrderStaff | null>(null)
  const [badgeHistory, setBadgeHistory] = useState<BadgeHistoryItem[]>([])
  const [generating, setGenerating] = useState(false)
  const [badgeData, setBadgeData] = useState<any>(null)
  const [templateConfig, setTemplateConfig] = useState<any>(null)
  const [badgeTrackingId, setBadgeTrackingId] = useState<number | string | null>(null)

  // Lock if this order is either fully "printed" (legacy manual complete) or
  // "completed" (auto-set once a bulk print run finishes — see bulkprint.tsx).
  // Both mean the backend will refuse to generate any more badges for it.
  const isOrderLocked = orderDetails?.status === "printed" || orderDetails?.status === "completed"

  // Ref to prevent multiple simultaneous or sequential completions
  const completingRef = useRef(false)

  // Fetch staff data for the order
  const fetchOrderStaff = async () => {
    setLoading(true)
    try {
      const data = await printOrderService.getOrderStaffForPrinting(order.id)
      setStaff(data.staff)
      setOrderDetails(data.order)
      setSummary(data.summary)
    } catch (error) {
      console.error("Error fetching order staff:", error)
      toast.error("Failed to load order staff")
    } finally {
      setLoading(false)
    }
  }

  // Fetch completion status
  const fetchCompletionStatus = async () => {
    try {
      const status = await printOrderService.checkOrderCompletionStatus(order.id)
      setCompletionStatus(status)
    } catch (error) {
      console.error("Error fetching completion status:", error)
    }
  }

  useEffect(() => {
    fetchOrderStaff()
    fetchCompletionStatus()
  }, [order.id])

  // Reopen ability (revert-to-printing) is admin/superuser only, same gate
  // used elsewhere in the app (e.g. StaffCars).
  useEffect(() => {
    const decodedToken = AuthService.getDecodedToken()
    if (decodedToken) {
      setIsPrivileged(!!(decodedToken.is_admin || decodedToken.is_superuser))
    }
  }, [])

  const fetchBadgeHistory = async (personId: number) => {
    try {
      const data = await ExhibitorRelatedService.getBadgeHistory(personId)
      if (data.success) {
        setBadgeHistory(data.badge_history)
      } else {
        toast.error("Failed to load badge history")
      }
    } catch (error) {
      console.error("Error fetching badge history:", error)
      toast.error("Failed to load badge history")
    }
  }

  // Modified requestBadgeData - handles original/copy logic based on badge history
  const requestBadgeData = async (staff: OrderStaff) => {
    if (isOrderLocked) {
      toast.warning("Cannot generate badges - This order is locked")
      return
    }

    setGenerating(true)
    try {
      // Determine print type and fee silently
      let printType: "original" | "copy"
      let confirmedFee = 0

      // Logic: Original is ONLY for first-time badge generation (total_badges_printed === 0)
      // If person has EVER had any badge before (even inactive), generate copy
      if (staff.total_badges_printed === 0) {
        printType = "original"
        confirmedFee = 0 // Original is free for first time only
      } else {
        printType = "copy"
        confirmedFee = staff.badge_reprint_fee || 0 // Copy uses reprint fee
      }

      const data = await ExhibitorRelatedService.requestBadge({
        representation_id: staff.id,
        print_order_id: order.id,
        print_type: printType,
        force_generation: false, // No forcing needed with this logic
        confirmed_fee: confirmedFee,
      })

      setBadgeData(data.badge_data)
      setTemplateConfig(data.template_config)
      setBadgeTrackingId(data.badge_tracking_id ?? null)
      setSelectedStaff(staff)
      setBadgeDesignerModal(true)
    } catch (error) {
      console.error("Error requesting badge:", error)
      toast.error("Failed to request badge data")
    } finally {
      setGenerating(false)
    }
  }

  const handleGenerateBadge = async (staff: OrderStaff) => {
    if (isOrderLocked) {
      toast.warning("Cannot generate badges - This order is locked")
      return
    }

    // Show single "Proceed to Generate" button with correct badge type info
    const result = await Swal.fire({
      title: "Generate Badge",
      icon: "question",
      iconColor: "#0d6efd",
      showCancelButton: true,
      confirmButtonText: `Proceed to Generate`,
      cancelButtonText: `Cancel`,
      confirmButtonColor: "#198754",
      cancelButtonColor: "#6c757d",
      width: "500px",
    })

    if (result.isConfirmed) {
      await requestBadgeData(staff)
    }
  }

  const handleViewHistory = async (staff: OrderStaff) => {
    setSelectedStaff(staff)
    await fetchBadgeHistory(staff.person_id)
    setHistoryModal(true)
  }

  // Modified badge completion handler to refresh data
  const handleBadgeComplete = async () => {
    try {
      // Refresh the order staff data after badge completion
      // await fetchOrderStaff()
      // await fetchCompletionStatus()
      // toast.success("Badge generated successfully!")
    } catch (error) {
      console.error("Error completing badge generation:", error)
    }
  }

  // Modified modal close handler to refresh data
  const handleDesignerModalClose = async () => {
    setBadgeDesignerModal(false)
    // Refresh data when modal closes
    await fetchOrderStaff()
    await fetchCompletionStatus()
  }

  // ---------------------------------------------------------------------------
  // handleSinglePrintSuccess
  //
  // Called by ProfessionalBadgeDesigner once a badge is physically printed
  // via QZ Tray. Refreshes staff data, then only completes (locks) the order
  // if EVERY staff member in the order now has at least one printed badge.
  //
  // This replaces the old behaviour that locked the order on the FIRST single
  // badge printed — which broke orders where bulk printing left a remainder
  // of badges that still needed individual A6 printing.
  // ---------------------------------------------------------------------------
  const handleSinglePrintSuccess = async () => {
    // Prevent multiple concurrent calls
    if (completingRef.current) return
    completingRef.current = true

    try {
      // Fetch fresh data — the state variable may be stale here because
      // requestBadgeData() created a new PersonBadgeTracking record right
      // before the badge designer opened, and we need that record counted.
      const freshData = await printOrderService.getOrderStaffForPrinting(order.id)
      setStaff(freshData.staff)
      setOrderDetails(freshData.order)
      setSummary(freshData.summary)

      // Count staff who still have no badge at all
      const pendingCount = freshData.staff.filter(s => s.total_badges_printed === 0).length
      const allPrinted = pendingCount === 0

      if (allPrinted) {
        // Every badge in this order has been printed — lock the order.
        await printOrderService.completePrintOrder(order.id)
        toast.info("All badges printed! Print order marked as completed.")
        // Re-fetch once more so the UI shows the locked state immediately.
        await fetchOrderStaff()
        await fetchCompletionStatus()
      } else {
        // Some badges are still pending — leave the order open.
        toast.success(
          `Badge printed! ${pendingCount} badge${pendingCount !== 1 ? "s" : ""} still pending.`
        )
        await fetchCompletionStatus()
      }
    } catch (error: any) {
      console.error("Failed to update print order after single print:", error)
      toast.warning(
        `Badge printed, but failed to update order status: ${error?.response?.data?.message || error?.message || "Unknown error"}`,
      )
    } finally {
      // Always reset so the next single print can check completion again.
      completingRef.current = false
    }
  }

  // Super user / admin only: reopens a completed/locked order so it becomes
  // printable again for other operators.
  const handleReopenOrder = async () => {
    const { value: reason } = await Swal.fire({
      title: "Reopen Print Order",
      html: `
        <div class="text-start p-3">
          <div class="alert alert-info">
            <h6>Order: ${orderDetails?.order_number}</h6>
            <p><strong>Company:</strong> ${orderDetails?.exhibitor_name}</p>
          </div>
          <div class="alert alert-warning">
            <p class="mb-0">This will unlock the order so other operators can generate badges against it again.</p>
          </div>
        </div>
      `,
      icon: "warning",
      input: "text",
      inputPlaceholder: "Reason (optional)",
      showCancelButton: true,
      confirmButtonText: "Reopen Order",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
    })

    if (reason === undefined) return // cancelled

    setLoadingCompletion(true)
    try {
      await printOrderService.revertPrintOrderToPrinting(order.id, reason || undefined)
      toast.success("Print order reopened for printing.")
      await fetchOrderStaff()
      await fetchCompletionStatus()
    } catch (error: any) {
      console.error("Error reopening order:", error)
      toast.error(error.response?.data?.message || "Failed to reopen order")
    } finally {
      setLoadingCompletion(false)
    }
  }

  const getBadgeStatusColor = (staff: OrderStaff) => {
    if (staff.total_badges_printed === 0) return "secondary"
    if (staff.has_original_badge && staff.last_badge_type === "original") return "success"
    if (staff.last_badge_type === "copy") return "info"
    return "primary"
  }

  const getBadgeStatusText = (staff: OrderStaff) => {
    if (staff.total_badges_printed === 0) return "No Badge"
    const type = staff.last_badge_type?.toUpperCase() || "UNKNOWN"
    return `${type} (${staff.total_badges_printed})`
  }

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case "paid":
        return "success"
      case "pending":
        return "warning"
      case "free":
        return "info"
      case "waived":
        return "secondary"
      default:
        return "secondary"
    }
  }

  // Enhanced DataTable columns
  const columns = [
    {
      name: "Photo",
      cell: (row: OrderStaff) => (
        <div className="position-relative">
          {row.has_profile_photo ? (
            <img
              src={row.profile_photo_url || "/placeholder.svg"}
              alt={row.person_full_name}
              className="rounded-circle shadow-sm"
              style={{
                width: "50px",
                height: "50px",
                objectFit: "cover",
                border: "3px solid #e9ecef",
              }}
            />
          ) : (
            <div
              className="rounded-circle d-flex align-items-center justify-content-center shadow-sm"
              style={{
                width: "50px",
                height: "50px",
                backgroundColor: "#f8f9fa",
                border: "3px solid #e9ecef",
              }}
            >
              <FaUser className="text-muted" size={20} />
            </div>
          )}
          {row.status && (
            <div
              className="position-absolute bg-success rounded-circle"
              style={{
                width: "12px",
                height: "12px",
                bottom: "2px",
                right: "2px",
                border: "2px solid white",
              }}
            />
          )}
        </div>
      ),
      width: "80px",
    },
    {
      name: "Staff Member",
      selector: (row: OrderStaff) => row.person_full_name,
      sortable: true,
      cell: (row: OrderStaff) => (
        <div>
          <div className="fw-bold text-dark">{row.person_full_name}</div>
          {/* <small className="text-muted d-block">{row.person_email}</small> */}
          {row.person_phone && <small className="text-muted d-block">{row.person_phone}</small>}
          {/* {row.person_id_number && <small className="text-muted d-block">{row.person_id_number}</small>} */}
        </div>
      ),
      width: "280px",
    },
    {
      name: "Role & Company",
      selector: (row: OrderStaff) => row.role,
      sortable: true,
      cell: (row: OrderStaff) => (
        <div>
          <span className="text-success">{row.role}</span>
          <div className="text-muted small">
            <FaBuilding className="me-1" />
            {row.exhibitor_name}
          </div>
        </div>
      ),
      width: "200px",
    },
    {
      name: "Exhibition",
      selector: (row: OrderStaff) => row.exhibition_title,
      sortable: true,
      cell: (row: OrderStaff) => (
        <div>
          <div className="fw-bold text-dark small">{row.exhibition_title}</div>
          <small className="text-muted">{row.stand_category}</small>
        </div>
      ),
      width: "160px",
    },
    {
      name: "Badge Status",
      sortable: true,
      cell: (row: OrderStaff) => (
        <div>
          <Badge color={getBadgeStatusColor(row)} pill className="mb-1">
            {getBadgeStatusText(row)}
          </Badge>

          {/* Print method indicator — only shown once a badge has been printed */}
          {row.total_badges_printed > 0 && row.print_method && (
            <div className="mt-1">
              <Badge
                color={row.is_bulk_printed ? "primary" : "secondary"}
                pill
                style={{ fontSize: "0.7rem" }}
              >
                {row.is_bulk_printed ? "Bulk" : "Single"}
              </Badge>
            </div>
          )}

          {/* Pending single-print indicator — badge held back from bulk run */}
          {row.total_badges_printed === 0 && !isOrderLocked && (
            <div className="mt-1">
              <Badge color="warning" pill style={{ fontSize: "0.7rem" }}>
                Pending A6
              </Badge>
            </div>
          )}

          {isOrderLocked && (
            <div>
              <Badge color="secondary" pill size="sm">
                <FaLock className="me-1" />
                Order Locked
              </Badge>
            </div>
          )}
          {row.last_printed_at && (
            <div className="text-muted small mt-1">Last: {new Date(row.last_printed_at).toLocaleDateString()}</div>
          )}
        </div>
      ),
      width: "180px",
    },
    {
      name: "Actions",
      cell: (row: OrderStaff) => (
        <div className="d-flex gap-1">
          <Button
            color={isOrderLocked ? "secondary" : "primary"}
            size="sm"
            onClick={() => handleGenerateBadge(row)}
            disabled={generating || isOrderLocked}
            title={isOrderLocked ? "This order is locked - Badge generation disabled" : "Generate Badge"}
            className={`shadow-sm ${isOrderLocked ? "opacity-50" : ""}`}
          >
            {isOrderLocked ? <FaLock size={14} /> : <FaIdBadge size={14} />}
          </Button>
          {row.total_badges_printed > 0 && (
            <Button
              color="info"
              size="sm"
              onClick={() => handleViewHistory(row)}
              title="View Badge History"
              className="shadow-sm"
            >
              <FaHistory size={14} />
            </Button>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "180px",
    },
  ]

  return (
    <div className="printing-staff-list">
      {/* Order Header */}
      <Row className="mb-4">
        <Col>
          <Card className={`border-0 shadow-sm ${isOrderLocked ? "bg-success" : "bg-primary"} text-white`}>
            <CardBody className="p-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-1 text-white">
                    <FaPrint className="me-2" />
                    Print Order: {orderDetails?.order_number}
                    {isOrderLocked && (
                      <Badge color="light" className="ms-2 text-success">
                        <FaCheckCircle className="me-1" />
                        COMPLETED
                      </Badge>
                    )}
                  </h5>
                  <div className="small opacity-75 tex-warning ">
                    <strong>{orderDetails?.exhibitor_name}</strong> • {orderDetails?.exhibition_title}
                  </div>
                  {/* {orderDetails?.pricing && (
                    <div className="small opacity-75 mt-1">
                      Badge Fee: {orderDetails.pricing.badge_fee} {orderDetails.pricing.currency} • Reprint:{" "}
                      {orderDetails.pricing.badge_reprint_fee} {orderDetails.pricing.currency}
                    </div>
                  )} */}
                </div>
                <div className="d-flex gap-2">
                  {isPrivileged && isOrderLocked && (
                    <Button
                      color="danger"
                      size="sm"
                      onClick={handleReopenOrder}
                      disabled={loadingCompletion}
                      className="d-flex align-items-center"
                    >
                      {loadingCompletion ? <Spinner size="sm" className="me-1" /> : <FaLock className="me-1" />}
                      Reopen Order
                    </Button>
                  )}
                  <Button color="primary" size="sm" onClick={onBackToSelection} className="d-flex align-items-center">
                    <FaArrowLeft className="me-1" />
                    Back to Orders
                  </Button>

                  {staff.length > 0 && !isOrderLocked && (() => {
                    // Compute the same split the bulk printer will use so the
                    // button label honestly reflects what will happen.
                    const BULK_PAGE_SIZE = 4
                    const bulkCount = Math.floor(staff.length / BULK_PAGE_SIZE) * BULK_PAGE_SIZE
                    const pendingCount = staff.length % BULK_PAGE_SIZE
                    // Count badges already pending (no badge at all yet)
                    const noBadgeCount = staff.filter(s => s.total_badges_printed === 0).length

                    return (
                      <div className="d-flex flex-column align-items-end gap-1">
                        <Button color="info" onClick={() => setBulkPrintModal(true)}>
                          <FaPrint className="me-2" />
                          {bulkCount > 0
                            ? `Bulk Print (${bulkCount}${pendingCount > 0 ? ` + ${pendingCount} single` : ""})`
                            : `All Single Print (${staff.length})`}
                        </Button>
                        {noBadgeCount > 0 && (
                          <small className="text-warning fw-semibold">
                            {noBadgeCount} badge{noBadgeCount !== 1 ? "s" : ""} pending
                          </small>
                        )}
                      </div>
                    )
                  })()}
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Enhanced Summary Cards */}
      {/* {summary && (
        <Row className="mb-4">
          <Col md="3">
            <Card className="border-0 shadow-sm h-100">
              <CardBody className="text-center p-4">
                <div
                  className="bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                  style={{ width: "60px", height: "60px" }}
                >
                  <FaUsers className="text-primary" size={28} />
                </div>
                <h3 className="text-primary fw-bold mb-1">{summary.total_staff}</h3>
                <p className="text-muted mb-0 fw-medium">Total Staff</p>
              </CardBody>
            </Card>
          </Col>
          <Col md="3">
            <Card className="border-0 shadow-sm h-100">
              <CardBody className="text-center p-4">
                <div
                  className="bg-success bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                  style={{ width: "60px", height: "60px" }}
                >
                  <FaCertificate className="text-success" size={28} />
                </div>
                <h3 className="text-success fw-bold mb-1">{summary.original_badges}</h3>
                <p className="text-muted mb-0 fw-medium">Original Badges</p>
              </CardBody>
            </Card>
          </Col>
          <Col md="3">
            <Card className="border-0 shadow-sm h-100">
              <CardBody className="text-center p-4">
                <div
                  className="bg-info bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                  style={{ width: "60px", height: "60px" }}
                >
                  <FaIdBadge className="text-info" size={28} />
                </div>
                <h3 className="text-info fw-bold mb-1">{summary.copy_badges}</h3>
                <p className="text-muted mb-0 fw-medium">Copy Badges</p>
              </CardBody>
            </Card>
          </Col>
          <Col md="3">
            <Card className="border-0 shadow-sm h-100">
              <CardBody className="text-center p-4">
                <div
                  className="bg-warning bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                  style={{ width: "60px", height: "60px" }}
                >
                  <FaCreditCard className="text-warning" size={28} />
                </div>
                <h3 className="text-warning fw-bold mb-1">
                  {orderDetails?.pricing
                    ? `${orderDetails.pricing.badge_reprint_fee} ${orderDetails.pricing.currency}`
                    : "Loading..."}
                </h3>
                <p className="text-muted mb-0 fw-medium">Reprint Fee</p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )} */}

      {/* Enhanced Staff Table */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="bg-white border-bottom">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0 fw-bold text-dark">
              <FaUsers className="me-2 text-primary" />
              Staff & Badge Management
              {isOrderLocked && (
                <Badge color="success" className="ms-2">
                  <FaLock className="me-1" size={12} />
                  Printing Locked
                </Badge>
              )}
            </h5>
            {/* <div className="text-end">
              <small className="text-muted d-block">Badge Reprint Fee:</small>
              <span className="fw-bold text-success">
                {orderDetails?.pricing
                  ? `${orderDetails.pricing.badge_reprint_fee} ${orderDetails.pricing.currency}`
                  : "Loading..."}
              </span>
            </div> */}
          </div>
        </CardHeader>
        <CardBody className="p-0">
          <DataTable
            columns={columns}
            data={staff}
            progressPending={loading}
            progressComponent={
              <div className="text-center py-5">
                <Spinner color="primary" size="lg" />
                <p className="mt-3 text-muted">Loading staff data...</p>
              </div>
            }
            pagination
            striped
            highlightOnHover
            customStyles={{
              headRow: {
                style: {
                  backgroundColor: "#f8f9fa",
                  borderBottom: "2px solid #dee2e6",
                  fontWeight: "bold",
                },
              },
              rows: {
                style: {
                  fontSize: "14px",
                  "&:hover": {
                    backgroundColor: "#f8f9fa",
                  },
                },
              },
            }}
            noDataComponent={
              <div className="text-center py-5">
                <FaUser size={48} className="mb-3 text-muted opacity-50" />
                <h6 className="text-muted">No staff members found</h6>
                <p className="text-muted small">Add staff members to start generating badges</p>
              </div>
            }
          />
        </CardBody>
      </Card>

      {/* Badge Designer Modal - Modified to use custom close handler */}
      <ProfessionalBadgeDesigner
        isOpen={badgeDesignerModal}
        onToggle={handleDesignerModalClose}
        badgeData={badgeData}
        templateConfig={templateConfig}
        onComplete={handleBadgeComplete}
        badgeTrackingId={badgeTrackingId}
        onPrintSuccess={handleSinglePrintSuccess}
      />

      {/* Enhanced Bulk Printer Modal */}
      <EnhancedBulkBadgePrinter
        isOpen={bulkPrintModal}
        onToggle={() => setBulkPrintModal(false)}
        staffList={staff}
        order={order}
        onOrderCompleted={() => {
          fetchOrderStaff()
          fetchCompletionStatus()
        }}
      />

      {/* Badge History Modal */}
      <Modal isOpen={historyModal} toggle={() => setHistoryModal(false)} size="xl">
        <ModalHeader toggle={() => setHistoryModal(false)} className="bg-info text-white">
          <FaHistory className="me-2" />
          Badge History - {selectedStaff?.person_full_name}
        </ModalHeader>
        <ModalBody className="p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-dark">
                <tr>
                  <th>Print Type</th>
                  <th>Sequence</th>
                  <th>Verification Code</th>
                  <th>Exhibition</th>

                  <th>Printed By</th>
                  <th>Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {badgeHistory.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center py-5">
                      <FaHistory size={48} className="text-muted opacity-50 mb-3" />
                      <div className="text-muted">
                        <h6>No badge history found</h6>
                        <p className="small mb-0">This staff member hasn't generated any badges yet.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  badgeHistory.map((badge) => (
                    <tr key={badge.id}>
                      <td>
                        <Badge color={badge.print_type === "original" ? "success" : "info"}>
                          {badge.print_type.toUpperCase()}
                        </Badge>
                      </td>
                      <td>
                        <code className="bg-light px-2 py-1 rounded">
                          #{badge.print_sequence.toString().padStart(4, "0")}
                        </code>
                      </td>
                      <td>
                        <code className="small bg-light px-2 py-1 rounded">{badge.verification_code}</code>
                      </td>
                      <td className="small">{badge.exhibition_title}</td>

                      <td className="small">{badge.printed_by_name}</td>
                      <td className="small">
                        {new Date(badge.printed_at).toLocaleDateString()}
                        <br />
                        <span className="text-muted">{new Date(badge.printed_at).toLocaleTimeString()}</span>
                      </td>
                      <td>
                        <Badge color={badge.is_active ? "success" : "secondary"}>
                          {badge.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </ModalBody>
      </Modal>

      <style>{`
        .swal-wide {
          width: 700px !important;
        }
        .printing-staff-list .table th {
          font-weight: 600;
          font-size: 0.875rem;
          text-transform: uppercase;
        }
        .badge-selection-modal {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }
        .badge-selection-modal .swal2-html-container {
          margin: 0.5em 0 !important;
        }
        .badge-selection-modal .card {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .badge-selection-modal .card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
      `}</style>
    </div>
  )
}

export default PrintingStaffListUpdated